import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetManageUserListFromADQuery,
  useGetManageUserRolesList,
  useGetServiceOfficeListByCountryCodesQuery,
} from "../../../../api/services/manageUser/manageUserApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiAutoComplete,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiMultiSelect,
  MuiRadioGroup,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
} from "../../../../components";
import { ADMIN_USER_GROUPS, TIMEOUT_300 } from "../../../../helpers/constants";
import {
  convertArrayTostring,
  filterUserRoles,
  getValueOrBlank,
  isNotEmpty,
  setApiDataToDropDownOption,
  setApiDataToDropDownOptionForRole,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  ADD_USER_TABS,
  CandidatePortalRole,
  defaultServiceOffice,
  Office,
  ServiceOfficeResultType,
  ServiceOfficeType,
  USER_ROLE_TYPES,
  UserRoleKey,
  ViewUserDataType,
} from "../../modelsAndSchemas/models";
import { CreateUserRoleDetailWrapper } from "./CreateUserRoleDetail.style";

import CloseIcon from "@mui/icons-material/Close";
import { UserRoleListType } from "../../../../api/services/manageUser/models";
import { FormAutoCompleteController } from "../../../../components/formController/FormAutoCompleteController";
import {
  MuiAlert,
  severityItems,
} from "../../../../components/muiAlert/MuiAlert";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { createUserDataSelector } from "../../../../redux/slices/manageUser/manageUserSelector";
import {
  setCreateUserDetail,
  setUserRoleList,
} from "../../../../redux/slices/manageUser/manageUserSlice";
import { createUserSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

interface CreateUserRoleDetailProps {
  pageId: string;
  countryListOptions: OptionsDataType[];
  selectedTab: number;
  isEditUser: boolean;
  editUserData?: ViewUserDataType;
  onPowerBiAccessCheckboxChange: (checked: boolean, fieldProps: any) => void;
  isCoachAdmin: boolean;
  hasPowerBIAccess: boolean;
  roleRef: React.RefObject<{ resetValues: (values?: string[]) => void }>;
}

export const CreateUserRoleDetail: FC<CreateUserRoleDetailProps> = ({
  pageId,
  countryListOptions,
  selectedTab,
  isEditUser,
  editUserData,
  onPowerBiAccessCheckboxChange,
  hasPowerBIAccess,
  isCoachAdmin,
  roleRef,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const storedUserDetail = useSelector(createUserDataSelector);
  const {
    control,
    setValue,
    reset: resetRoleDetailForm,
    watch,
    getValues,
  } = useFormContext();
  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const countryRef = useRef<{ resetValues: (values?: string[]) => void }>(null);
  const servicesOfficeRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);

  const [alertMessage, setAlertMessage] = useState({
    powerBiAlert: false,
    coachAlert: false,
  });

  const [inputValue, setInputValue] = useState("");
  const debouncedValue = useDebounce(inputValue, TIMEOUT_300);
  const [options, setOptions] = useState([]);
  const [isSelectedOffice, setIsSelectedOffice] = useState(false);

  const {
    data: manageUserRolesList,
    isLoading: isManageUserRolesListLoading,
    isError: isManageUserRolesListError,
  } = useGetManageUserRolesList();

  const {
    data: adUserList,
    isFetching: isUserVerifiedDetailsLoading,
    refetchWithParams: refetchUserVerifiedDetails,
  } = useGetManageUserListFromADQuery();

  const {
    data: serviceOfficeListByCountryCodes,
    refetchWithParams: refetchServiceOfficeList,
    isFetching: isServiceOfficeFetching,
    isError: isServiceOfficeError,
  } = useGetServiceOfficeListByCountryCodesQuery();

  const clearUserData = () => {
    if (!isEditUser) {
      setValue(UserRoleKey.firstName, "");
      setValue(UserRoleKey.lastName, "");
      setValue(UserRoleKey.activeDirectoryId, "");
    }
  };

  const clearServiceOfficeData = () => {
    const { servicesOffice, country } = getValues();

    const list = serviceOfficeListByCountryCodes ?? [];

    const matchedOffices: Office[] = list.filter(
      (item: Office) =>
        servicesOffice?.includes(item.id) &&
        country?.includes(item.countryCode),
    );

    const serviceOffice = matchedOffices.length
      ? matchedOffices.map((x: Office) => x.id)
      : [];

    servicesOfficeRef.current?.resetValues(serviceOffice);
    setValue(UserRoleKey.servicesOffice, serviceOffice);
  };

  const serviceOfficeOptions = useMemo(() => {
    return setApiDataToDropDownOption(serviceOfficeListByCountryCodes);
  }, [serviceOfficeListByCountryCodes]);

  useEffect(() => {
    const { country } = getValues();
    if (isNotEmpty(country)) {
      refetchServiceOfficeList({
        countryCode: country ? convertArrayTostring(country) : "",
      });
    }
    clearServiceOfficeData();
  }, [watch(UserRoleKey.country)]);

  useEffect(() => {
    if (isManageUserRolesListError || isServiceOfficeError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isManageUserRolesListError, isServiceOfficeError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isManageUserRolesListLoading ||
          isUserVerifiedDetailsLoading ||
          isServiceOfficeFetching,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isManageUserRolesListLoading,
    isUserVerifiedDetailsLoading,
    isServiceOfficeFetching,
  ]);

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const getUserRoleLabel = (key: string) =>
    fetchLabel(createUserSiteCoreKeys.userRoleKey, key);

  const userRolesOptions = useMemo(() => {
    let candidatePortalRoles: OptionsDataType[] = [];
    let clientPortalRoles: OptionsDataType[] = [];
    if (isNotEmpty(manageUserRolesList)) {
      candidatePortalRoles = setApiDataToDropDownOptionForRole(
        filterUserRoles(manageUserRolesList, [ADMIN_USER_GROUPS.G4]),
      );
      clientPortalRoles = setApiDataToDropDownOptionForRole(
        filterUserRoles(manageUserRolesList, [ADMIN_USER_GROUPS.G3]),
      );
    }
    dispatch(setUserRoleList([...candidatePortalRoles] as UserRoleListType[]));

    return { candidatePortalRoles, clientPortalRoles };
  }, [manageUserRolesList]);

  const setSelectedServiceOffice = () => {
    const servicesOffice =
      editUserData?.adminUserRoles?.serviceOffices
        ?.filter((office: ServiceOfficeType) => office.officeId)
        .map((office: ServiceOfficeType) => office.officeId) || [];
    setValue(UserRoleKey.servicesOffice, servicesOffice);

    setTimeout(() => {
      servicesOfficeRef.current?.resetValues(servicesOffice);
    }, TIMEOUT_300);
  };

  useEffect(() => {
    if (
      isNotEmpty(editUserData) &&
      isNotEmpty(serviceOfficeOptions) &&
      !isSelectedOffice
    ) {
      setIsSelectedOffice(true);
      setSelectedServiceOffice();
    }
  }, [editUserData, serviceOfficeOptions]);

  useEffect(() => {
    const { clientPortalRole, servicesOffice } = getValues();
    if (selectedTab !== ADD_USER_TABS.ROLE_DETAIL) {
      const serviceOfficeList =
        serviceOfficeListByCountryCodes
          ?.filter((office: ServiceOfficeResultType) =>
            servicesOffice.includes(office?.id),
          )
          ?.map((office: ServiceOfficeResultType) => ({
            CountryCode: getValueOrBlank(office?.countryCode),
            OfficeId: getValueOrBlank(office?.id),
            OfficeName: getValueOrBlank(office?.name),
            // default values for service office
            ...defaultServiceOffice,
          })) ?? [];
      dispatch(
        setCreateUserDetail({
          ...storedUserDetail,
          clientPortalRoleText: isNotEmpty(clientPortalRole)
            ? (userRolesOptions.clientPortalRoles.find(
                (role: OptionsDataType) => role.value === clientPortalRole,
              )?.label ?? "")
            : "",
          serviceOfficeList: serviceOfficeList,
        }),
      );
    }
  }, [selectedTab]);

  const getCountryCodeFromEdit = () => {
    const codes =
      editUserData?.adminUserRoles?.serviceOffices?.map(
        (office: ServiceOfficeType) => office?.countryCode?.toLowerCase(),
      ) || [];

    return Array.from(new Set(codes.filter(Boolean)));
  };

  const getRoleType = (roleId: string[], clientPortalRoleId: string) => {
    const hasCandidatePortalRole = isNotEmpty(roleId);
    const hasClientPortalRole = isNotEmpty(clientPortalRoleId);

    if (hasCandidatePortalRole && hasClientPortalRole)
      return USER_ROLE_TYPES.ALL;
    else if (hasClientPortalRole) return USER_ROLE_TYPES.CLIENT_PORTAL;
    else return USER_ROLE_TYPES.CIRRUS_P3_AND_CANDIDATE_PORTAL;
  };
  //edit user data handling
  useEffect(() => {
    if (
      isEditUser &&
      isNotEmpty(editUserData) &&
      isNotEmpty(countryListOptions)
    ) {
      const roleId: string[] =
        editUserData?.adminUserRoles?.candidatePortalRole
          ?.map((role: CandidatePortalRole) => role.roleId)
          .filter((id): id is string => id !== undefined) || [];

      const clientPortalRoleId = getValueOrBlank(
        editUserData?.adminUserRoles?.clientPortalRole?.roleId,
      );
      resetRoleDetailForm({
        [UserRoleKey.userEmail]: getValueOrBlank(editUserData?.emailAddress),
        [UserRoleKey.firstName]: getValueOrBlank(editUserData?.firstName),
        [UserRoleKey.lastName]: getValueOrBlank(editUserData?.lastName),
        [UserRoleKey.activeDirectoryId]: getValueOrBlank(
          editUserData?.activeDirectoryId,
        ),
        [UserRoleKey.role]: roleId,
        [UserRoleKey.clientPortalRole]: clientPortalRoleId,
        [UserRoleKey.country]: getCountryCodeFromEdit(),
        [UserRoleKey.servicesOffice]: [],
        [UserRoleKey.hasAccessToPowerBIReport]:
          editUserData?.hasAccessToPowerBIReport,
        [UserRoleKey.roleType]: getRoleType(roleId, clientPortalRoleId),
      });
      roleRef.current?.resetValues(roleId);
      countryRef.current?.resetValues(getCountryCodeFromEdit());
      setSelectedServiceOffice();
    }
  }, [editUserData, countryListOptions]);

  useEffect(() => {
    if (debouncedValue.length >= 3) {
      refetchUserVerifiedDetails({ email: debouncedValue });
    }
    setOptions([]);
    clearUserData();
  }, [debouncedValue]);

  useEffect(() => {
    if (isNotEmpty(adUserList)) {
      const options = adUserList.map((user: ViewUserDataType) => ({
        id: user.emailAddress,
        label: user.emailAddress,
      }));
      setOptions(options);
    } else {
      setOptions([]);
      clearUserData();
    }
  }, [adUserList]);

  useEffect(() => {
    const { userEmail } = getValues();
    if (isNotEmpty(userEmail) && isNotEmpty(adUserList)) {
      const userDetails = adUserList?.find(
        (user: ViewUserDataType) => user.emailAddress === userEmail,
      );
      setValue(UserRoleKey.firstName, getValueOrBlank(userDetails?.firstName));
      setValue(UserRoleKey.lastName, getValueOrBlank(userDetails?.lastName));
      setValue(UserRoleKey.activeDirectoryId, getValueOrBlank(userDetails?.id));
    }
  }, [watch(UserRoleKey.userEmail), adUserList]);

  useEffect(() => {
    setAlertMessage((prevState) => ({
      ...prevState,
      powerBiAlert: hasPowerBIAccess,
    }));
  }, [hasPowerBIAccess]);

  useEffect(() => {
    setAlertMessage((prevState) => ({
      ...prevState,
      coachAlert: isCoachAdmin,
    }));
  }, [isCoachAdmin]);

  const radioOptions = [
    {
      value: USER_ROLE_TYPES.CIRRUS_P3_AND_CANDIDATE_PORTAL,
      label: getUserRoleLabel(
        createUserSiteCoreKeys.cirrusP3AndCandidatePortalRoleRadioButton,
      ),
    },
    {
      value: USER_ROLE_TYPES.CLIENT_PORTAL,
      label: getUserRoleLabel(createUserSiteCoreKeys.clientPortalRole),
    },
    {
      value: USER_ROLE_TYPES.ALL,
      label: getUserRoleLabel(createUserSiteCoreKeys.allRoleRadioButton),
    },
  ];

  const roleType = useMemo(() => {
    const { roleType } = getValues();
    if (roleType === USER_ROLE_TYPES.ALL)
      return {
        showClientPortalRole: true,
        showCirrusP3AndCandidatePortalRole: true,
      };
    else if (roleType === USER_ROLE_TYPES.CLIENT_PORTAL)
      return {
        showClientPortalRole: true,
        showCirrusP3AndCandidatePortalRole: false,
      };
    else
      return {
        showClientPortalRole: false,
        showCirrusP3AndCandidatePortalRole: true,
      };
  }, [watch(UserRoleKey.roleType)]);

  useEffect(() => {
    if (roleType.showCirrusP3AndCandidatePortalRole) {
      roleRef.current?.resetValues(getValues(UserRoleKey.role));
    }
  }, [roleType.showCirrusP3AndCandidatePortalRole]);


  const handleUserRoleTypeChange = () => {
    const clientPortalRoleValue = getValues(UserRoleKey.clientPortalRole);
    const roleValue = getValues(UserRoleKey.role);
    setValue(UserRoleKey.clientPortalRole, clientPortalRoleValue);
    setValue(UserRoleKey.role, roleValue);
  };

  return (
    <CreateUserRoleDetailWrapper>
      <MuiGrid container className="user-role-email-grid">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          {isEditUser ? (
            <FormController control={control} name={UserRoleKey.userEmail}>
              <MuiTextField
                id={`${pageId}-${UserRoleKey.userEmail}`}
                label={getUserRoleLabel(createUserSiteCoreKeys.userEmail)}
                disabled
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          ) : (
            <FormAutoCompleteController
              control={control}
              name={UserRoleKey.userEmail}
            >
              <MuiAutoComplete
                label={getUserRoleLabel(createUserSiteCoreKeys.userEmail)}
                placeholder={commonLabels.typeToSearchPlaceholder3Chars}
                requiredARIALabel={commonARIALabel.required}
                isMandatory
                options={options}
                showCloseIcon
                inputValue={inputValue}
                setInputValue={setInputValue}
                isInputValueControlled
                id={`${pageId}-${UserRoleKey.userEmail}`}
                disabled={isEditUser}
                noOptionsText={commonLabels.noResult}
              />
            </FormAutoCompleteController>
          )}
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController control={control} name={UserRoleKey.firstName}>
            <MuiTextField
              id={`${pageId}-${UserRoleKey.firstName}`}
              label={getUserRoleLabel(createUserSiteCoreKeys.firstName)}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController control={control} name={UserRoleKey.lastName}>
            <MuiTextField
              id={`${pageId}-${UserRoleKey.lastName}`}
              label={getUserRoleLabel(createUserSiteCoreKeys.lastName)}
              showCloseIcon
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiBox>
        <AccordionHeader
          className="accordion-header"
          title={renderHeader(
            getUserRoleLabel(createUserSiteCoreKeys.userRolesSectionTitle),
          )}
          isExpanded={true}
          showExpandCollapseIcon={false}
        >
          <MuiBox className="accordion-container  role-accordion-header">
            <MuiTypography
              variant="h2SemiBold"
              className="role-header-text  role-header-text-create"
              component="div"
            >
              {getUserRoleLabel(
                createUserSiteCoreKeys.cirrusP3AndCandidatePortalAndClientPortalSubTitle,
              )}
            </MuiTypography>
            <MuiBox>
              <FormController control={control} name={UserRoleKey.roleType}>
                <MuiRadioGroup
                  label={""}
                  value={getValues(UserRoleKey.roleType)}
                  onChange={handleUserRoleTypeChange}
                  options={radioOptions}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiBox>
            <MuiGrid container>
              {roleType.showCirrusP3AndCandidatePortalRole && (
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <FormController
                    control={control}
                    name={UserRoleKey.role}
                    ref={roleRef}
                  >
                    <MuiMultiSelect
                      label={getUserRoleLabel(
                        createUserSiteCoreKeys.cirrusP3AndCandidatePortalRoleRadioButton,
                      )}
                      options={userRolesOptions.candidatePortalRoles}
                      showSearch
                      showChip
                      isMandatory
                      id={`${pageId}-${UserRoleKey.role}`}
                      chipId={`${pageId}-${UserRoleKey.role}-chip`}
                      dialogTitle={getUserRoleLabel(
                        createUserSiteCoreKeys.selectedRoles,
                      )}
                      isDialogTitleWithEllipsis
                      selectPlaceHolder={commonLabels.selectPlaceHolder}
                      searchPlaceHolder={commonLabels.search}
                      selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                      doneText={commonLabels.done}
                      allARIAProps={multiSelectARIALabels}
                    />
                  </FormController>
                </MuiGrid>
              )}
              {roleType.showClientPortalRole && (
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <FormController
                    control={control}
                    name={UserRoleKey.clientPortalRole}
                  >
                    <MuiSelect
                      label={getUserRoleLabel(
                        createUserSiteCoreKeys.clientPortalRole,
                      )}
                      options={userRolesOptions.clientPortalRoles}
                      visibleDefaultSelectOption
                      value=""
                      id={`${pageId}-${UserRoleKey.clientPortalRole}`}
                      placeholder={commonLabels.selectPlaceHolder}
                      isMandatory
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
              )}
            </MuiGrid>

            {alertMessage.coachAlert && (
              <MuiBox className="coach-alert-box">
                <MuiAlert
                  alertTitle={commonLabels.note}
                  message={getUserRoleLabel(
                    createUserSiteCoreKeys.coachRoleNoteMessage,
                  )}
                  severity={severityItems.warning}
                  iconAreaLabel="Close"
                  iconPosition="end"
                  icon={<CloseIcon />}
                  onIconClick={() =>
                    setAlertMessage((prevState) => ({
                      ...prevState,
                      coachAlert: false,
                    }))
                  }
                />
              </MuiBox>
            )}
          </MuiBox>
        </AccordionHeader>
        <AccordionHeader
          className="accordion-header"
          title={renderHeader(
            getUserRoleLabel(
              createUserSiteCoreKeys.roleDetailsLocationAccessSectionTitle,
            ),
          )}
          isExpanded={true}
          showExpandCollapseIcon={false}
        >
          <MuiGrid container className="accordion-container location-access">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={UserRoleKey.country}
                ref={countryRef}
              >
                <MuiMultiSelect
                  label={getUserRoleLabel(createUserSiteCoreKeys.country)}
                  options={countryListOptions}
                  showSearch
                  showChip
                  isMandatory
                  id={`${pageId}-${UserRoleKey.country}`}
                  chipId={`${pageId}-${UserRoleKey.country}-chip`}
                  dialogTitle={getUserRoleLabel(
                    createUserSiteCoreKeys.selectedCountries,
                  )}
                  isDialogTitleWithEllipsis
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={UserRoleKey.servicesOffice}
                ref={servicesOfficeRef}
              >
                <MuiMultiSelect
                  label={getUserRoleLabel(createUserSiteCoreKeys.serviceOffice)}
                  options={serviceOfficeOptions}
                  showSearch
                  showChip
                  isMandatory
                  id={`${pageId}-${UserRoleKey.servicesOffice}`}
                  chipId={`${pageId}-${UserRoleKey.servicesOffice}-chip`}
                  dialogTitle={getUserRoleLabel(
                    createUserSiteCoreKeys.selectedServiceOffices,
                  )}
                  isDialogTitleWithEllipsis
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </AccordionHeader>
        <AccordionHeader
          className="accordion-header"
          title={renderHeader(
            getUserRoleLabel(
              createUserSiteCoreKeys.powerBIReportAccessSectionTitle,
            ),
          )}
          isExpanded={true}
          showExpandCollapseIcon={false}
        >
          <MuiGrid container className="accordion-container power-bi-container">
            <MuiBox className="power-bi-access-checkbox-container">
              <Controller
                name={UserRoleKey.hasAccessToPowerBIReport}
                control={control}
                render={({ field }) => (
                  <MuiCheckbox
                    {...field}
                    checked={field.value}
                    checkboxLabel={getUserRoleLabel(
                      createUserSiteCoreKeys.provideAccessForPowerBIReportsCheckBoxText,
                    )}
                    id={`${pageId}-${UserRoleKey.hasAccessToPowerBIReport}`}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      onPowerBiAccessCheckboxChange(e.target.checked, field);
                    }}
                  />
                )}
              />
            </MuiBox>
            {alertMessage.powerBiAlert && (
              <MuiBox className="power-bi-alert-box">
                <MuiAlert
                  alertTitle={commonLabels.note}
                  message={getUserRoleLabel(
                    createUserSiteCoreKeys.powerBIReportAccessNoteMessage,
                  )}
                  severity={severityItems.warning}
                  iconAreaLabel="Close"
                  iconPosition="end"
                  icon={<CloseIcon />}
                  onIconClick={() =>
                    setAlertMessage((prevState) => ({
                      ...prevState,
                      powerBiAlert: false,
                    }))
                  }
                />
              </MuiBox>
            )}
          </MuiGrid>
        </AccordionHeader>
      </MuiBox>
    </CreateUserRoleDetailWrapper>
  );
};
